<template>
  <Transition
    enter-active-class="ease-out duration-300"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="ease-in duration-200"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div v-if="show" class="fixed inset-0 z-[9999] overflow-y-auto">
      <div
        class="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0"
      >
        <Transition
          enter-active-class="ease-out duration-300"
          enter-from-class="opacity-0"
          enter-to-class="opacity-100"
          leave-active-class="ease-in duration-200"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <div
            class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            @click="$emit('close')"
          />
        </Transition>

        <span
          class="hidden sm:inline-block sm:h-screen sm:align-middle"
          aria-hidden="true"
        >&#8203;</span>

        <Transition
          enter-active-class="ease-out duration-300"
          enter-from-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enter-to-class="opacity-100 translate-y-0 sm:scale-100"
          leave-active-class="ease-in duration-200"
          leave-from-class="opacity-100 translate-y-0 sm:scale-100"
          leave-to-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        >
          <div
            class="inline-block transform overflow-hidden rounded-2xl bg-white px-4 pt-5 pb-4 text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6 sm:align-middle"
          >
            <div class="absolute top-0 right-0 pt-4 pr-4">
              <button
                type="button"
                class="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 ring-brand focus:ring-offset-2"
                @click="$emit('close')"
              >
                <span class="sr-only">Fermer</span>
                <svg
                  class="h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div class="sm:flex sm:items-start">
              <div
                class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-brand/10 sm:mx-0 sm:h-10 sm:w-10"
              >
                <svg
                  class="h-6 w-6 text-brand"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                  />
                </svg>
              </div>
              <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <h3
                  class="text-lg font-['Roboto_Black'] leading-6 text-gray-900"
                >
                  Récupération d'identifiants
                </h3>
                <div class="mt-2">
                  <p class="text-sm font-['Roboto_Regular'] text-gray-500">
                    Entrez votre adresse email AGCO pour recevoir vos
                    identifiants.
                  </p>
                </div>
              </div>
            </div>

            <form class="mt-6" @submit.prevent="handleSubmit">
              <div class="space-y-4">
                <!-- Mode normal : recherche par email -->
                <div v-if="!showUuidMode">
                  <label
                    for="email"
                    class="block text-sm font-['Roboto_Medium'] text-gray-700"
                  >
                    Email AGCO
                  </label>
                  <div class="mt-1">
                    <input
                      id="email"
                      v-model="email"
                      type="email"
                      required
                      class="block w-full rounded-xl border-gray-300 shadow-sm focus:border-brand ring-brand sm:text-sm font-['Roboto_Regular']"
                      placeholder="votre.nom@agcocorp.com"
                      @input="onEmailInput"
                    >
                  </div>
                </div>

                <!-- Mode UUID : recherche par identifiant -->
                <div v-else>
                  <label
                    for="uuid"
                    class="block text-sm font-['Roboto_Medium'] text-gray-700"
                  >
                    Identifiant (UUID)
                  </label>
                  <div class="mt-1">
                    <input
                      id="uuid"
                      v-model="uuid"
                      type="text"
                      required
                      class="block w-full rounded-xl border-gray-300 shadow-sm focus:border-brand ring-brand sm:text-sm font-['Roboto_Regular']"
                      placeholder="Votre identifiant"
                    >
                  </div>
                </div>

                <!-- Bouton pour basculer entre les modes -->
                <div class="text-center">
                  <button
                    type="button"
                    class="text-sm font-['Roboto_Regular'] text-brand hover:text-brand/80 underline"
                    @click="toggleMode"
                  >
                    {{ showUuidMode ? 'J\'ai mon email' : 'Je n\'ai pas mon email' }}
                  </button>
                </div>

                <!-- Selection du compte si plusieurs comptes partagent le même email -->
                <div v-if="accounts.length > 0" class="rounded-xl bg-blue-50 p-4">
                  <p class="mb-3 text-sm font-['Roboto_Medium'] text-gray-900">
                    Plusieurs comptes utilisent cet email. Sélectionnez le compte à réinitialiser :
                  </p>
                  <div class="space-y-2">
                    <button
                      v-for="(account, index) in accounts"
                      :key="account.uuid || index"
                      type="button"
                      class="w-full rounded-lg border-2 px-3 py-2 text-left transition-colors"
                      :class="selectedAccountUuid === account.uuid
                        ? 'border-brand bg-brand/10 text-brand'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-brand/50'"
                      @click="selectedAccountUuid = account.uuid"
                    >
                      <p class="text-sm font-['Roboto_Medium']">{{ account.name || 'Compte' }}</p>
                      <p class="text-xs font-['Roboto_Regular'] text-gray-500">{{ account.group || 'Aucune franchise' }}</p>
                    </button>
                  </div>
                </div>

                <!-- Affichage des emails trouvés (mode UUID) -->
                <div v-if="foundEmails.length > 0" class="rounded-xl bg-blue-50 p-4">
                  <p class="text-sm font-['Roboto_Medium'] text-gray-900 mb-3">
                    Sélectionnez l'email sur lequel recevoir vos identifiants :
                  </p>
                  <div class="space-y-2">
                    <button
                      v-for="(foundEmail, index) in foundEmails"
                      :key="index"
                      type="button"
                      class="w-full text-left px-3 py-2 rounded-lg border-2 transition-colors font-['Roboto_Regular'] text-sm"
                      :class="selectedEmail === foundEmail
                        ? 'border-brand bg-brand/10 text-brand'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-brand/50'"
                      @click="selectedEmail = foundEmail"
                    >
                      {{ foundEmail }}
                    </button>
                  </div>
                </div>

                <div v-if="error" class="rounded-xl bg-red-50 p-4">
                  <p class="text-sm font-['Roboto_Regular'] text-brand">
                    {{ error }}
                  </p>
                </div>

                <div v-if="success && foundEmails.length === 0 && accounts.length === 0" class="rounded-xl bg-green-50 p-4">
                  <p class="text-sm font-['Roboto_Regular'] text-green-800">
                    {{ success }}
                  </p>
                </div>
              </div>

              <div class="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  class="inline-flex justify-center rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-['Roboto_Medium'] text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 ring-brand focus:ring-offset-2"
                  @click="$emit('close')"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  :disabled="loading || (!showUuidMode && accounts.length > 0 && !selectedAccountUuid)"
                  class="inline-flex justify-center rounded-xl border border-transparent bg-brand px-4 py-2 text-sm font-['Roboto_Medium'] text-white shadow-sm hover:bg-brand/90 focus:outline-none focus:ring-2 ring-brand focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span v-if="loading" class="flex items-center">
                    <svg
                      class="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                      />
                      <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Envoi en cours...
                  </span>
                  <span v-else>Envoyer</span>
                </button>
              </div>
            </form>
          </div>
        </Transition>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref } from "@vue/runtime-core";
import axios from "axios";

type Account = {
  uuid: string;
  name: string;
  group: string;
};

defineProps<{
  show: boolean;
}>();

const emit = defineEmits<{
  (e: "close"): void;
}>();

const email = ref("");
const uuid = ref("");
const loading = ref(false);
const error = ref("");
const success = ref("");
const showUuidMode = ref(false);
const foundEmails = ref<string[]>([]);
const selectedEmail = ref<string | null>(null);
const accounts = ref<Account[]>([]);
const selectedAccountUuid = ref<string | null>(null);

const resetAccountSelection = () => {
  accounts.value = [];
  selectedAccountUuid.value = null;
};

const toggleMode = () => {
  showUuidMode.value = !showUuidMode.value;
  error.value = "";
  success.value = "";
  foundEmails.value = [];
  selectedEmail.value = null;
  resetAccountSelection();
};

const onEmailInput = () => {
  error.value = "";
  success.value = "";
  if (accounts.value.length > 0) {
    resetAccountSelection();
  }
};

const handleSubmit = async () => {
  try {
    loading.value = true;
    error.value = "";
    success.value = "";

    if (showUuidMode.value) {
      foundEmails.value = [];

      // Mode UUID : rechercher les emails associés
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL_FULL}forgot-password/find-emails`,
        { uuid: uuid.value.trim() },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      foundEmails.value = response.data.emails;
      success.value = response.data.message;
    } else {
      const requestData: { email: string; account_uuid?: string } = {
        email: email.value.trim(),
      };

      if (accounts.value.length > 0) {
        if (!selectedAccountUuid.value) {
          error.value = "Veuillez sélectionner le compte à réinitialiser.";
          return;
        }

        requestData.account_uuid = selectedAccountUuid.value;
      }

      const response = await axios.post(
        `${import.meta.env.VITE_API_URL_FULL}forgot-password`,
        requestData,
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      success.value = response.data.message;
      resetAccountSelection();
      setTimeout(() => {
        emit("close");
      }, 2000);
    }
  } catch (err: any) {
    const responseData = err.response?.data;

    // Nouveau flow : plusieurs comptes pour le même email
    if (
      !showUuidMode.value &&
      err.response?.status === 409 &&
      responseData?.multiple_accounts
    ) {
      accounts.value = Array.isArray(responseData.accounts)
        ? responseData.accounts
        : [];
      selectedAccountUuid.value = null;
      error.value =
        responseData.message ||
        "Plusieurs comptes ont été trouvés pour cet email. Sélectionnez un compte.";
      return;
    }

    // Ancien flow (compatibilité)
    if (responseData?.requires_uuid) {
      error.value = responseData.message;
      showUuidMode.value = true;
    } else {
      error.value =
        responseData?.message ||
        "Une erreur est survenue lors de l'envoi de l'email.";
    }
  } finally {
    loading.value = false;
  }
};
</script>

<script lang="ts">
export default {
  name: "ForgotPasswordModal",
};
</script>
